import { TextInput } from "@jugl-web/ui-components";
import { cx, useFormFieldsValidators } from "@jugl-web/utils";
import parsePhoneNumber, { CountryCode } from "libphonenumber-js";
import { CSSProperties, FC, HTMLInputTypeAttribute } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { ChatButton } from "../../../ChatButton";

export interface ChatBotInputsFormProps {
  inputs: {
    name: string;
    type: HTMLInputTypeAttribute;
    isRequired?: boolean;
  }[];
  className?: string;
  style?: CSSProperties;
  onSubmit: (data: { name: string; value: string }[]) => void;
}

interface FormValues {
  fields: { value: string }[];
}

const DEFAULT_COUNTRY_CODE: CountryCode = "US";

// Normalize phone number to E.164 (country code + digits only)
// so equivalent inputs identify the same lead on the backend
const normalizePhoneNumber = (
  phoneNumberValue: string,
  defaultCountry: CountryCode
): string =>
  parsePhoneNumber(phoneNumberValue, defaultCountry)?.format("E.164") ??
  phoneNumberValue;

export const ChatBotInputsForm: FC<ChatBotInputsFormProps> = ({
  inputs,
  className,
  style,
  onSubmit,
}) => {
  const { control, formState, register, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      fields: inputs.map(() => ({ value: "" })),
    },
  });

  const { fields } = useFieldArray({ control, name: "fields" });

  const { requiredValidator, emailValidator, phoneNumberValidator } =
    useFormFieldsValidators();

  const handleFormSubmit = handleSubmit((data) => {
    const transformedData = data.fields.map((field, index) => {
      const inputDef = inputs[index];

      let { value } = field;

      if (inputDef.type === "tel" && field.value) {
        value = normalizePhoneNumber(field.value, DEFAULT_COUNTRY_CODE);
      }

      return {
        name: inputs[index].name,
        value,
      };
    });

    onSubmit(transformedData);
  });

  return (
    <form
      className={cx("flex w-full flex-col gap-4", className)}
      style={style}
      onSubmit={handleFormSubmit}
      noValidate
    >
      <div className="flex w-full flex-col gap-4">
        {fields.map((field, index) => {
          const inputDef = inputs[index];
          const fieldError = formState.errors.fields?.[index]?.value;

          return (
            <TextInput
              key={field.id}
              type={inputDef.type}
              isRequired={inputDef.isRequired}
              labelPosition="border-overlap"
              size="default"
              placeholder="Enter"
              label={inputDef.name}
              isInvalid={!!fieldError}
              tooltip={
                inputDef.type === "tel" ? (
                  <span className="text-sm">
                    Use a valid phone number. Non-US numbers must include a
                    country code (e.g. +48…)
                  </span>
                ) : undefined
              }
              errorMessage={fieldError?.message}
              {...register(`fields.${index}.value`, {
                ...(inputDef.isRequired && requiredValidator()),
                ...(inputDef.type === "email" && emailValidator()),
                ...(inputDef.type === "tel" &&
                  phoneNumberValidator({
                    defaultCountry: DEFAULT_COUNTRY_CODE,
                  })),
              })}
            />
          );
        })}
      </div>
      <ChatButton type="submit" variant="primary" size="md" fullWidth>
        Send
      </ChatButton>
    </form>
  );
};
